import { ElementType, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import CreateNewFolderOutlined from "@mui/icons-material/CreateNewFolderOutlined";
import FileUploadOutlined from "@mui/icons-material/FileUploadOutlined";
import { Alert, Box, Stack, Container, Typography as T, Button } from "@mui/material";
import { TypographyProps } from "@mui/material/Typography";
import CreateFolderDialog from "files/components/CreateFolderDialog";
import FileBreadcrumbs from "files/components/FileBreadcrumbs";
import FileJobs from "files/components/FileJobs";
import FileTitle from "files/components/FileTitle";
import useBasePath from "files/hooks/useBasePath";
import useFileAccess from "files/hooks/useFileAccess";
import useFiles, { useCurrentKey, useFilesInfinite, useFilesSearch } from "files/hooks/useFiles";
import BaseDirectory from "files/types/enums/baseDirectory";
import FileInfo from "files/types/interfaces/fileInfo";
import queryString from "query-string";
import { BooleanParam, StringParam, useQueryParam, useQueryParams } from "use-query-params";
import ContextualSearch from "~/components/ContextualSearch";
import ScrollableTabContent from "~/components/ScrollableTabContent";
import { Directory as ShadcnDirectory } from "~/components/ui/directory";
import FileNotFound from "./FileNotFound";
import FilePreview from "./FilePreview";
import SearchResults from "./SearchResults";
import UploadFileDialog from "./UploadFileDialog";

const SearchTitle = (props: { component?: ElementType } & TypographyProps) => (
  <T
    component="h1"
    {...props}
    sx={{
      fontSize: 20,
      fontWeight: 600,
      overflowWrap: "break-word",
      lineHeight: 1.2,
    }}
  />
);

const Header = () => {
  const basePath = useBasePath();
  const navigate = useNavigate();
  const currentKey = useCurrentKey();
  const [newFolderDialogOpen, setNewFolderDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const canWrite = useFileAccess(currentKey, "write");
  const showUpload = canWrite && currentKey !== BaseDirectory.ORG;
  const { isLoading: searchLoading } = useFilesSearch();
  const [{ q: query, preview }] = useQueryParams({ q: StringParam, preview: BooleanParam });
  const searchDirLabel = <FileTitle fileKey={currentKey} />;

  const title = useMemo(() => {
    if (query) {
      if (!currentKey) {
        return <SearchTitle>Results</SearchTitle>;
      }
      return <SearchTitle>Results in &quot;{searchDirLabel}&quot;</SearchTitle>;
    }

    return <FileBreadcrumbs />;
  }, [query, preview, currentKey]);

  return (
    <Container maxWidth="lg" sx={{ pt: 2 }}>
      <Box display="flex" justifyContent="space-between" gap={1} alignItems="center" minHeight={40}>
        {title}
        <Box display="flex" gap={1} alignItems="center">
          <FileJobs />
          {showUpload && (
            <>
              <Button
                variant="outlined"
                startIcon={<CreateNewFolderOutlined />}
                onClick={() => setNewFolderDialogOpen(true)}
              >
                New Folder
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileUploadOutlined />}
                onClick={() => setUploadDialogOpen(true)}
              >
                Upload Files
              </Button>
            </>
          )}
        </Box>
      </Box>
      <Box mt={1} mb={2}>
        <ContextualSearch
          loading={searchLoading}
          currentPathLabel={currentKey && searchDirLabel}
          submitCallback={({ query, root }) => {
            const qs = queryString.stringify({ q: query });

            if (root) {
              navigate({ pathname: basePath, search: qs });
              return;
            }
          }}
        />
      </Box>
      <CreateFolderDialog
        open={newFolderDialogOpen}
        onClose={() => setNewFolderDialogOpen(false)}
      />
      <UploadFileDialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} />
    </Container>
  );
};

const Files = () => {
  const { state } = useLocation();
  const preview = state?.preview as Pick<FileInfo, "key" | "size"> | undefined;
  const [search] = useQueryParam("q", StringParam);
  const {
    data: files,
    allChildren,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMore,
    error: getFileListError,
  } = useFilesInfinite();

  const body = useMemo(() => {
    if (getFileListError) {
      return (
        <Alert severity="error" variant="outlined">
          Unable to load files
        </Alert>
      );
    }

    if (search) {
      return <SearchResults />;
    }

    if (!isLoading && !files?.current_file) {
      return <FileNotFound />;
    }

    return (
      <ShadcnDirectory
        navigation="url-based"
        files={allChildren}
        loading={isLoading}
        showExternalBuckets
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={loadMore}
      />
    );
  }, [files, allChildren, search, getFileListError, isLoading, hasMore, isLoadingMore, loadMore]);

  return (
    <ScrollableTabContent bgcolor="common.dimmedPageBackground" sx={{ minWidth: 750 }}>
      <Header />
      <Container maxWidth="lg" sx={{ pb: 3 }}>
        {body}
      </Container>
      {preview && <FilePreview file={preview} />}
    </ScrollableTabContent>
  );
};

export default Files;
